<script setup>
/* global axios */
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import HotelBookingsAPI from 'dashboard/api/hotelBookings';

const { t } = useI18n();

const bookings = ref([]);
const isLoading = ref(false);

const filters = ref({
  status: '',
  payment_status: '',
  date_from: '',
  date_to: '',
  guest_name: '',
  source: '',
});

const statuses = [
  { value: '', label: t('MOTOR_RESERVA.COMMON.ALL') },
  { value: 'budget', label: t('MOTOR_RESERVA.BOOKINGS.STATUS.BUDGET') },
  { value: 'confirmed', label: t('MOTOR_RESERVA.BOOKINGS.STATUS.CONFIRMED') },
  { value: 'checked_in', label: t('MOTOR_RESERVA.BOOKINGS.STATUS.CHECKED_IN') },
  {
    value: 'checked_out',
    label: t('MOTOR_RESERVA.BOOKINGS.STATUS.CHECKED_OUT'),
  },
  { value: 'cancelled', label: t('MOTOR_RESERVA.BOOKINGS.STATUS.CANCELLED') },
];

const paymentStatuses = [
  { value: '', label: t('MOTOR_RESERVA.COMMON.ALL') },
  {
    value: 'payment_pending',
    label: t('MOTOR_RESERVA.BOOKINGS.PAYMENT_STATUS.PAYMENT_PENDING'),
  },
  {
    value: 'partial',
    label: t('MOTOR_RESERVA.BOOKINGS.PAYMENT_STATUS.PARTIAL'),
  },
  { value: 'paid', label: t('MOTOR_RESERVA.BOOKINGS.PAYMENT_STATUS.PAID') },
  {
    value: 'refunded',
    label: t('MOTOR_RESERVA.BOOKINGS.PAYMENT_STATUS.REFUNDED'),
  },
];

const sources = [
  { value: '', label: t('MOTOR_RESERVA.COMMON.ALL') },
  { value: 'admin', label: 'Admin' },
  { value: 'booking_engine', label: 'Booking Engine' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const checkingPaymentId = ref(null);
const asaasCheckResult = ref(null);

const statusLabel = status => {
  const labels = {
    budget: t('MOTOR_RESERVA.BOOKINGS.STATUS.BUDGET'),
    confirmed: t('MOTOR_RESERVA.BOOKINGS.STATUS.CONFIRMED'),
    checked_in: t('MOTOR_RESERVA.BOOKINGS.STATUS.CHECKED_IN'),
    checked_out: t('MOTOR_RESERVA.BOOKINGS.STATUS.CHECKED_OUT'),
    cancelled: t('MOTOR_RESERVA.BOOKINGS.STATUS.CANCELLED'),
  };
  return labels[status] || status;
};

const statusClass = status => {
  const classes = {
    budget: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-green-100 text-green-700',
    checked_in: 'bg-blue-100 text-blue-700',
    checked_out: 'bg-slate-100 text-slate-600',
    cancelled: 'bg-red-100 text-red-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-500';
};

const paymentStatusLabel = status => {
  const labels = {
    payment_pending: 'Pendente',
    partial: 'Parcial',
    paid: 'Pago',
    refunded: 'Reembolsado',
  };
  return labels[status] || status || '-';
};

const paymentStatusClass = status => {
  const classes = {
    payment_pending: 'bg-amber-100 text-amber-700',
    partial: 'bg-orange-100 text-orange-700',
    paid: 'bg-emerald-100 text-emerald-700',
    refunded: 'bg-purple-100 text-purple-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-500';
};

const roomDisplay = booking => {
  return booking.room_display_name || booking.hotel_room?.name || '-';
};

const confirmPaymentManual = async booking => {
  if (
    !confirm(
      'Confirmar pagamento manualmente? Esta acao nao pode ser desfeita.'
    )
  )
    return;
  try {
    const { data } = await HotelBookingsAPI.confirmPaymentManual(booking.id);
    const idx = bookings.value.findIndex(b => b.id === booking.id);
    if (idx >= 0) {
      bookings.value[idx].payment_status = data.payment_status;
      bookings.value[idx].status = data.status;
      bookings.value[idx].payment_method = data.payment_method;
    }
  } catch (error) {
    asaasCheckResult.value = {
      id: booking.id,
      error: error?.response?.data?.error || 'Erro ao confirmar pagamento.',
    };
  }
};

const checkAsaasPayment = async booking => {
  if (!booking.asaas_payment_id) {
    asaasCheckResult.value = {
      id: booking.id,
      error: 'Sem cobrança Asaas vinculada.',
    };
    return;
  }
  checkingPaymentId.value = booking.id;
  asaasCheckResult.value = null;
  try {
    const { data } = await HotelBookingsAPI.checkAsaasPayment(booking.id);
    asaasCheckResult.value = { id: booking.id, ...data };
    // Update booking in list if status changed
    if (data.payment_status === 'paid') {
      const idx = bookings.value.findIndex(b => b.id === booking.id);
      if (idx >= 0) {
        bookings.value[idx].payment_status = data.payment_status;
        bookings.value[idx].status = data.booking_status;
      }
    }
  } catch (error) {
    asaasCheckResult.value = {
      id: booking.id,
      error:
        error?.response?.data?.error || error?.message || 'Erro ao consultar.',
    };
  } finally {
    checkingPaymentId.value = null;
  }
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const formatCurrency = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const guestDisplay = booking => {
  if (booking.contact) {
    return booking.contact.name || booking.contact.email || '-';
  }
  return booking.guest_name || '-';
};

const fetchBookings = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (filters.value.status) params.status = filters.value.status;
    if (filters.value.payment_status)
      params.payment_status = filters.value.payment_status;
    if (filters.value.date_from) params.date_from = filters.value.date_from;
    if (filters.value.date_to) params.date_to = filters.value.date_to;
    if (filters.value.guest_name) params.guest_name = filters.value.guest_name;
    if (filters.value.source) params.source = filters.value.source;

    const response = await axios.get(HotelBookingsAPI.url, { params });
    bookings.value = response.data;
  } catch (error) {
    bookings.value = [];
  } finally {
    isLoading.value = false;
  }
};

const clearFilters = () => {
  filters.value = {
    status: '',
    payment_status: '',
    date_from: '',
    date_to: '',
    guest_name: '',
    source: '',
  };
  fetchBookings();
};

const filteredBookings = computed(() => bookings.value);

onBeforeMount(() => {
  fetchBookings();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div>
      <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
        {{ t('MOTOR_RESERVA.BOOKINGS.HEADER') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-300">
        {{ t('MOTOR_RESERVA.BOOKINGS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Filters -->
    <div
      class="grid grid-cols-6 gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600"
    >
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.STATUS') }}
        </label>
        <select
          v-model="filters.status"
          class="px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
        >
          <option v-for="s in statuses" :key="s.value" :value="s.value">
            {{ s.label }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.PAYMENT_STATUS') }}
        </label>
        <select
          v-model="filters.payment_status"
          class="px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
        >
          <option
            v-for="ps in paymentStatuses"
            :key="ps.value"
            :value="ps.value"
          >
            {{ ps.label }}
          </option>
        </select>
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.DATE_FROM') }}
        </label>
        <input
          v-model="filters.date_from"
          type="date"
          class="px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.DATE_TO') }}
        </label>
        <input
          v-model="filters.date_to"
          type="date"
          class="px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.GUEST_NAME') }}
        </label>
        <input
          v-model="filters.guest_name"
          type="text"
          :placeholder="t('MOTOR_RESERVA.COMMON.SEARCH_PLACEHOLDER')"
          class="px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-slate-500 dark:text-slate-400">
          {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.SOURCE') }}
        </label>
        <select
          v-model="filters.source"
          class="px-2 py-1.5 border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
        >
          <option v-for="src in sources" :key="src.value" :value="src.value">
            {{ src.label }}
          </option>
        </select>
      </div>
    </div>
    <div class="flex gap-2">
      <button
        class="px-3 py-1.5 text-sm bg-woot-500 text-white rounded hover:bg-woot-600"
        @click="fetchBookings"
      >
        {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.SEARCH') }}
      </button>
      <button
        class="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-500 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
        @click="clearFilters"
      >
        {{ t('MOTOR_RESERVA.BOOKINGS.FILTERS.CLEAR') }}
      </button>
    </div>

    <!-- Table -->
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="text-slate-500">{{
        t('MOTOR_RESERVA.COMMON.LOADING')
      }}</span>
    </div>

    <div v-else class="overflow-x-auto">
      <table
        class="w-full text-sm border border-slate-200 dark:border-slate-600 rounded-lg"
      >
        <thead>
          <tr class="bg-slate-50 dark:bg-slate-700">
            <th
              class="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.ID') }}
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.GUEST') }}
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.ROOM') }}
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.CHECK_IN') }}
            </th>
            <th
              class="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.CHECK_OUT') }}
            </th>
            <th
              class="px-3 py-2 text-center text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.NIGHTS') }}
            </th>
            <th
              class="px-3 py-2 text-center text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.STATUS') }}
            </th>
            <th
              class="px-3 py-2 text-center text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              Pagamento
            </th>
            <th
              class="px-3 py-2 text-right text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              {{ t('MOTOR_RESERVA.BOOKINGS.TABLE.TOTAL') }}
            </th>
            <th
              class="px-3 py-2 text-center text-xs font-medium text-slate-500 dark:text-slate-300 uppercase"
            >
              Asaas
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="booking in filteredBookings"
            :key="booking.id"
            class="border-t border-slate-100 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-750"
          >
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              #{{ booking.id }}
            </td>
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              <div>{{ guestDisplay(booking) }}</div>
              <div v-if="booking.guest_phone" class="text-xs text-slate-400">
                {{ booking.guest_phone }}
              </div>
            </td>
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              {{ roomDisplay(booking) }}
            </td>
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              {{ formatDate(booking.check_in) }}
            </td>
            <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
              {{ formatDate(booking.check_out) }}
            </td>
            <td
              class="px-3 py-2 text-center text-slate-700 dark:text-slate-200"
            >
              {{ booking.nights || '-' }}
            </td>
            <td class="px-3 py-2 text-center">
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="statusClass(booking.status)"
              >
                {{ statusLabel(booking.status) }}
              </span>
            </td>
            <td class="px-3 py-2 text-center">
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="paymentStatusClass(booking.payment_status)"
              >
                {{ paymentStatusLabel(booking.payment_status) }}
              </span>
              <div
                v-if="booking.payment_method"
                class="text-[10px] text-slate-400 mt-0.5 uppercase"
              >
                {{ booking.payment_method }}
              </div>
            </td>
            <td
              class="px-3 py-2 text-right font-medium text-slate-800 dark:text-slate-100"
            >
              {{ formatCurrency(booking.total_price) }}
            </td>
            <td class="px-3 py-2 text-center">
              <button
                v-if="booking.asaas_payment_id"
                class="px-2 py-1 text-xs rounded border transition-colors"
                :class="
                  checkingPaymentId === booking.id
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-wait'
                    : 'bg-white text-woot-500 border-woot-300 hover:bg-woot-50'
                "
                :disabled="checkingPaymentId === booking.id"
                :title="
                  'Verificar pagamento no Asaas (ID: ' +
                  booking.asaas_payment_id +
                  ')'
                "
                @click="checkAsaasPayment(booking)"
              >
                {{
                  checkingPaymentId === booking.id
                    ? 'Verificando...'
                    : 'Verificar'
                }}
              </button>
              <button
                v-if="booking.payment_status !== 'paid'"
                class="px-2 py-1 text-xs rounded border bg-white text-amber-600 border-amber-300 hover:bg-amber-50 transition-colors mt-1"
                :title="
                  booking.asaas_payment_id
                    ? 'Confirmar manualmente e cancelar cobranca Asaas'
                    : 'Confirmar pagamento manualmente (Admin)'
                "
                @click="confirmPaymentManual(booking)"
              >
                Pago (Manual)
              </button>
              <span
                v-else-if="!booking.asaas_payment_id"
                class="text-xs text-slate-300"
                >—</span>
              <!-- Result popover -->
              <div
                v-if="asaasCheckResult && asaasCheckResult.id === booking.id"
                class="mt-1 text-xs text-left"
              >
                <div v-if="asaasCheckResult.error" class="text-red-500">
                  {{ asaasCheckResult.error }}
                </div>
                <div v-else>
                  <span
                    class="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold"
                    :class="
                      asaasCheckResult.asaas_status === 'RECEIVED' ||
                      asaasCheckResult.asaas_status === 'CONFIRMED'
                        ? 'bg-emerald-100 text-emerald-700'
                        : asaasCheckResult.asaas_status === 'PENDING'
                          ? 'bg-amber-100 text-amber-700'
                          : asaasCheckResult.asaas_status === 'OVERDUE'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-slate-100 text-slate-600'
                    "
                  >
                    {{ asaasCheckResult.asaas_status }}
                  </span>
                  <a
                    v-if="asaasCheckResult.invoice_url"
                    :href="asaasCheckResult.invoice_url"
                    target="_blank"
                    class="text-woot-500 hover:underline ml-1"
                    >Fatura</a>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <p
      v-if="!isLoading && filteredBookings.length === 0"
      class="text-sm text-slate-500 text-center py-4"
    >
      {{ t('MOTOR_RESERVA.BOOKINGS.EMPTY') }}
    </p>
  </div>
</template>
